import { CommandListOrgUsers, type OrgUser } from "./commands/cmdListOrgUsers";
import { CommandListProfiles, type Profile } from "./commands/cmdListProfiles";
import { CommandListRoles, type Role } from "./commands/cmdListRoles";
import { CommandAddUserToOrg } from "./commands/cmdAddUserToOrg";
import { CommandCreateUserInOrg, type CreateUserInOrgPayload } from "./commands/cmdCreateUserInOrg";
import { CommandRemoveUserFromOrg } from "./commands/cmdRemoveUserFromOrg";
import { CommandSetUserActive } from "./commands/cmdSetUserActive";
import { CommandListOrgUserVersions } from "./commands/cmdListOrgUserVersions";
import { CommandUpdateProfileRoles } from "./commands/cmdUpdateProfileRoles";
import { CommandListFeatures } from "./commands/cmdListFeatures";
import { CommandCreateFeature, type Feature } from "./commands/cmdCreateFeature";
import { CommandUpdateFeature } from "./commands/cmdUpdateFeature";
import { CommandDeleteFeature } from "./commands/cmdDeleteFeature";
import { CommandCreateRole } from "./commands/cmdCreateRole";
import { CommandGetRole, type RoleDetail } from "./commands/cmdGetRole";
import { CommandUpdateRole } from "./commands/cmdUpdateRole";
import { CommandDeleteRole } from "./commands/cmdDeleteRole";
import { CommandSetRolePermissions } from "./commands/cmdSetRolePermissions";
import { CommandGetUserPermissions, type UserPermissions } from "./commands/cmdGetUserPermissions";
import type { AggregateVersion } from "@models/aggregateVersion.model";

export class PermissionService {
  // Org users
  async listOrgUsers(orgId: number): Promise<OrgUser[]> {
    return new CommandListOrgUsers().run(orgId);
  }

  // Profiles
  async listProfiles(): Promise<Profile[]> {
    return new CommandListProfiles().run();
  }

  async updateProfileRoles(profileId: number, roleIds: number[]): Promise<Profile> {
    return new CommandUpdateProfileRoles().run(profileId, roleIds);
  }

  async addUserToOrg(profileId: number, orgId: number): Promise<void> {
    return new CommandAddUserToOrg().run(profileId, orgId);
  }

  async createUserInOrg(orgId: number, payload: CreateUserInOrgPayload): Promise<OrgUser> {
    return new CommandCreateUserInOrg().run(orgId, payload);
  }

  async removeUserFromOrg(profileId: number, orgId: number): Promise<void> {
    return new CommandRemoveUserFromOrg().run(profileId, orgId);
  }

  async setUserActive(orgId: number, profileId: number, isActive: boolean): Promise<OrgUser> {
    return new CommandSetUserActive().run(orgId, profileId, isActive);
  }

  async listOrgUserVersions(orgId: number): Promise<AggregateVersion[]> {
    return new CommandListOrgUserVersions().run(orgId);
  }

  // Features
  async listFeatures(): Promise<Feature[]> {
    return new CommandListFeatures().run();
  }

  async createFeature(payload: { name: string; parent_id?: number | null }): Promise<Feature> {
    return new CommandCreateFeature().run(payload);
  }

  async updateFeature(featureId: number, payload: { name?: string; parent_id?: number | null }): Promise<Feature> {
    return new CommandUpdateFeature().run(featureId, payload);
  }

  async deleteFeature(featureId: number): Promise<void> {
    return new CommandDeleteFeature().run(featureId);
  }

  // Roles
  async listRoles(organizationId?: number): Promise<Role[]> {
    return new CommandListRoles().run(organizationId);
  }

  async createRole(payload: { name: string }): Promise<Role> {
    return new CommandCreateRole().run(payload);
  }

  async getRole(roleId: number): Promise<RoleDetail> {
    return new CommandGetRole().run(roleId);
  }

  async updateRole(roleId: number, payload: { name?: string }): Promise<Role> {
    return new CommandUpdateRole().run(roleId, payload);
  }

  async deleteRole(roleId: number): Promise<void> {
    return new CommandDeleteRole().run(roleId);
  }

  async setRolePermissions(roleId: number, permissions: { feature_id: number; level: number }[]): Promise<RoleDetail> {
    return new CommandSetRolePermissions().run(roleId, permissions);
  }

  async getUserPermissions(userId: number): Promise<UserPermissions> {
    return new CommandGetUserPermissions().run(userId);
  }
}
